import React, { useEffect, useRef, useState } from "react";
import Quagga from "quagga"; // npm install quagga
import axios from "axios";
import { API_URL } from "../../utils/constant";
import Modal from "../../components/modal";
import Swal from "sweetalert2"; // npm install sweetalert2

const BarcodeScanner = () => {
  const scannerRef = useRef(null);
  const [scannedCode, setScannedCode] = useState("");
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [showScanner, setShowScanner] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const inputRef = useRef(null);
  const [formData, setFormData] = useState({
    raw_product_id: "",
    product_name: "",
    product_code: "",
    item_number: "",
    supplier_id: "",
    purchase_date: "",
    purchase_by: "",
    is_expiry_available: false,
    expiry_month_year: "",
    is_expired: false,
    unit: "",
    quantity: "",
    damaged: "",
    return_prod: "",
    variant_stock: []
  });
  const [variations, setVariations] = useState([]);
  const [selectedVariations, setSelectedVariations] = useState([]);
  const [tagInputs, setTagInputs] = useState({}); // Track tag inputs for each row

  // For USB barcode scanner input
  const [keyboardBuffer, setKeyboardBuffer] = useState("");

  useEffect(() => {
    if (showScanner && scannerRef.current) {
      Quagga.init(
        {
          inputStream: {
            name: "Live",
            type: "LiveStream",
            target: scannerRef.current,
            constraints: {
              width: 1280,
              height: 720,
              facingMode: "environment",
              aspectRatio: { min: 1, max: 2 }
            },
          },
          locator: {
            patchSize: "large",
            halfSample: false,
          },
          numOfWorkers: 2,
          frequency: 5,
          decoder: {
            readers: [
              "code_128_reader", // Primary focus on Code128
              "code_39_reader",  // Secondary support for Code39
            ],
            multiple: false, // Only detect one barcode at a time
          },
          locate: true,
        },
        (err) => {
          if (err) {
            console.error("Quagga init error:", err);
            setError("Failed to initialize scanner. Please check camera permissions.");
            return;
          }
          console.log("Quagga initialized successfully");
          Quagga.start();
          setIsScanning(true);
        }
      );

      Quagga.onDetected((data) => {
        const code = data?.codeResult?.code;
        const format = data?.codeResult?.format;
        const confidence = data?.codeResult?.confidence;
        
        console.log("=== BARCODE DETECTION DEBUG ===");
        console.log("Detected code:", code);
        console.log("Barcode format:", format);
        console.log("Confidence level:", confidence);
        console.log("Raw codeResult:", data?.codeResult);
        console.log("Raw data:", data);
        console.log("================================");

        if (code) {
          console.log("=== BARCODE PROCESSING ===");
          console.log("Processing barcode:", code);
          console.log("Current scanned code:", scannedCode);
          console.log("Is scanning:", isScanning);
          
          // Only accept Code128 and Code39 formats
          if (format === 'code_128' || format === 'code_39') {
            console.log("Format is supported:", format);
            
            // Check confidence level - reject low confidence scans
            // if (confidence && confidence < 0.5) {
            //   console.warn("Low confidence scan detected:", confidence);
            //   setError("Low confidence scan. Please try again with better lighting/positioning.");
            //   return;
            // }
            
            // console.log("Confidence is acceptable:", confidence);
            
            // Validate the scanned code format
            console.log("Barcode format is valid, calling fetchProduct...");
            setScannedCode(code);
            Quagga.stop();
            setIsScanning(false);
            setShowScanner(false);
            // Automatically call API when barcode is scanned
            fetchProduct(code);
          } else {
            console.warn("Unsupported barcode format:", format, "Code:", code);
            setError(`Unsupported barcode format: ${format}. Please use Code128 or Code39 barcodes.`);
            // Continue scanning for supported formats
          }
          console.log("=== END BARCODE PROCESSING ===");
        }
      });

      Quagga.onProcessed((data) => {
        if (data) {
          const drawingCanvas = Quagga.canvas.dom.image;
          if (drawingCanvas) {
            const context = drawingCanvas.getContext('2d');
            if (data.codeResult && data.codeResult.code) {
              Quagga.ImageDebug.drawPath(data.line, { x: 'x', y: 'y' }, context, { color: 'green', lineWidth: 5 });
            }
          }
        }
      });
    }

    return () => {
      Quagga.stop();
      Quagga.offDetected();
      Quagga.offProcessed();
      // Remove keyboard event listener
      window.removeEventListener('keydown', handleKeyboardInput);
    };
  // Add keyboard event listener for USB scanner
  }, [showScanner, isScanning]);

  // useEffect(() => {
  //   if (showScanner) {
  //     window.addEventListener('keydown', handleKeyboardInput);
  //   } else {
  //     window.removeEventListener('keydown', handleKeyboardInput);
  //   }
  //   return () => {
  //     window.removeEventListener('keydown', handleKeyboardInput);
  //   };
  //   // eslint-disable-next-line
  // }, [showScanner]);

  useEffect(() => {
    const focusInput = () => {
      inputRef.current?.focus();
    };

    focusInput();
    window.addEventListener('focus', focusInput);

    return () => window.removeEventListener('focus', focusInput);
  }, []);

  // Handler for USB barcode scanner (keyboard input)
  function handleKeyboardInput(e) {
    // Ignore if a modal or input is focused
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) {
      return;
    }
    // Only process if scanner is visible and not already scanning
    if (!showScanner || isScanning) return;
    // Most USB barcode scanners type the code and then send Enter
    if (e.key === 'Enter') {
      if (keyboardBuffer.length > 2) {
        console.log("Keyboard input detected, processing code:", keyboardBuffer);
        setScannedCode(keyboardBuffer);
        fetchProduct(keyboardBuffer);
      }
      setKeyboardBuffer("");
    } else if (e.key.length === 1) {
      // Only add printable characters
      setKeyboardBuffer(prev => prev + e.key);
    }
  }

  // Function to validate barcode format
  // const isValidBarcodeFormat = (code) => {
  //   if (!code || typeof code !== 'string') return false;
    
  //   console.log('Validating barcode:', code);
  //   console.log('Barcode length:', code.length);
    
  //   // Check if barcode contains only alphanumeric characters and hyphens
  //   const isValidChars = /^[A-Za-z0-9\-]+$/.test(code);
  //   console.log('Barcode contains valid characters:', isValidChars);
    
  //   // Remove any special characters and normalize
  //   const cleanCode = code.replace(/[^\w\-]/g, '');
  //   console.log('Cleaned barcode:', cleanCode);
    
  //   // Check if it matches the expected format: SKU-VENDOR-PRODUCTNAME
  //   // Should contain at least 2 hyphens and be alphanumeric
  //   const parts = cleanCode.split('-');
  //   console.log('Barcode parts:', parts);
    
  //   // Primary validation: Must have at least 2 hyphens (SKU-VENDOR-PRODUCT)
  //   if (parts.length >= 2 && cleanCode.length > 5 && isValidChars) {
  //     console.log('Valid barcode format detected (SKU-VENDOR-PRODUCT)');
  //     return true;
  //   }
    
  //   // Secondary validation: Simple alphanumeric codes (for testing)
  //   if (isValidChars && code.length > 3 && parts.length >= 1) {
  //     console.log('Valid simple barcode format detected');
  //     return true;
  //   }
    
  //   console.log('Invalid barcode format - does not match expected pattern');
  //   return false;
  // };

  const fetchProduct = async (code) => {
    console.log("=== API CALL DEBUG ===");
    console.log("Fetching product for code:", code);
    console.log("API URL:", `${API_URL}/raw-products/product/by-barcode?code=${encodeURIComponent(code)}`);
    
    try {
      setError(null);
      const res = await axios.get(`${API_URL}/raw-products/product/by-barcode?code=${encodeURIComponent(code)}`);
      
      console.log("API Response:", res.data);
      
      if (res.data && res.data.length > 0) {
        const productData = res.data[0];
        setProduct(productData);
        console.log("Product found:", productData);
        
        // Initialize form data with product information
        setFormData({
          raw_product_id: productData.id || "",
          product_name: productData.name || "",
          product_code: productData.sku || "",
          item_number: "",
          supplier_id: productData.supplier_id || "",
          purchase_date: new Date().toISOString().split('T')[0],
          purchase_by: "",
          is_expiry_available: false,
          expiry_month_year: "",
          is_expired: false,
          unit: productData.unit || "",
          quantity: productData.quantity || "",
          damaged: "",
          return_prod: "",
          variant_stock: []
        });
        
        // Generate variations if product has variants
        if (productData.variants && productData.product_type === 'variants') {
          generateVariations(productData.variants);
        }
        
        // Show the form
        setShowForm(true);
      } else {
        setProduct(null);
        setError("Product not found for this barcode.");
        console.log("No products found in response");
        // Show reload option instead of form
        setShowForm(false);
      }
    } catch (err) {
      console.error("API error:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      
      setProduct(null);
      if (err.response?.status === 404) {
        setError("Product not found for this barcode.");
      } else {
        setError("Error fetching product. Please try again.");
      }
      // Show reload option instead of form
      setShowForm(false);
    }
    console.log("=== END API CALL DEBUG ===");
  };

  const generateVariations = (variants) => {
    try {
      let parsedVariants = variants;
      if (typeof variants === 'string') {
        parsedVariants = JSON.parse(variants);
      }
      
      if (Array.isArray(parsedVariants) && parsedVariants.length > 0) {
        const combos = cartesian(parsedVariants);
        setVariations(combos);
      }
    } catch (error) {
      console.error('Error generating variations:', error);
      setVariations([]);
    }
  };

  // Utility to generate cartesian product of variants
  function cartesian(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return [];
    return arr.reduce((a, b) =>
      a.flatMap(d => Array.isArray(b.selectedTags) ? b.selectedTags.map(e => [...d, { attributeId: b.attributeId, value: e }]) : a)
    , [[]]);
  }

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addVariation = () => {
    setSelectedVariations(prev => [...prev, {
      combination: null,
      quantity: '',
      serial_no: [],
      price: ''
    }]);
  };

  const removeVariation = (index) => {
    setSelectedVariations(prev => prev.filter((_, i) => i !== index));
  };

  const updateVariation = (index, field, value) => {
    setSelectedVariations(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Handle tag input changes
  const handleTagInputChange = (rowIndex, value) => {
    setTagInputs(prev => ({
      ...prev,
      [rowIndex]: value
    }));
  };

  // Handle tag input key press (Enter to add tag)
  const handleTagInputKeyPress = (rowIndex, e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const newTag = e.target.value.trim();
      const updated = [...selectedVariations];
      if (!Array.isArray(updated[rowIndex].serial_no)) {
        updated[rowIndex].serial_no = [];
      }
      
      // Check if quantity is set and if adding this tag would exceed the quantity
      const currentQuantity = parseInt(updated[rowIndex].quantity) || 0;
      const currentSerialCount = updated[rowIndex].serial_no.length;
      
      if (currentQuantity > 0 && currentSerialCount >= currentQuantity) {
        alert(`Cannot add more serial numbers. Quantity is ${currentQuantity} and you already have ${currentSerialCount} serial numbers.`);
        return;
      }
      
      if (!updated[rowIndex].serial_no.includes(newTag)) {
        updated[rowIndex].serial_no.push(newTag);
      }
      setSelectedVariations(updated);
      setTagInputs(prev => ({
        ...prev,
        [rowIndex]: ''
      }));
    }
  };

  // Remove tag
  const removeTag = (rowIndex, tagToRemove) => {
    const updated = [...selectedVariations];
    updated[rowIndex].serial_no = updated[rowIndex].serial_no.filter(tag => tag !== tagToRemove);
    setSelectedVariations(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const company = JSON.parse(localStorage.getItem('company'));
      const companyId = company.company?.id;
      const formDataObj = new FormData();

      // Append existing form data to FormData (excluding variant_stock)
      Object.keys(formData).forEach(key => {
        if (key !== 'variant_stock') { // Don't append variant_stock here
          formDataObj.append(key, formData[key]);
        }
      });

      // Append company_id
      formDataObj.append('company_id', companyId);
      
      // If variations are selected, add them as variant_stock
      if (selectedVariations.length > 0) {
        // Filter out invalid variations (no combination or no quantity)
        const validVariations = selectedVariations.filter(variation => 
          variation.combination && 
          variation.quantity && 
          variation.quantity > 0
        );
        
        console.log('=== FRONTEND VARIANT DATA ===');
        console.log('Original selectedVariations:', selectedVariations);
        console.log('Filtered validVariations:', validVariations);
        console.log('JSON.stringify(validVariations):', JSON.stringify(validVariations));
        console.log('================================');
        
        if (validVariations.length > 0) {
          formDataObj.append('variant_stock', JSON.stringify(validVariations));
        }
      }

      const response = await axios.post(`${API_URL}/raw-material-stock`, formDataObj);
      console.log('Data submitted successfully:', response.data);
      
      // Close the form
      setShowForm(false);
      
      // Show success message with SweetAlert
      Swal.fire({
        title: 'Success!',
        text: 'Raw material stock has been added successfully.',
        icon: 'success',
        confirmButtonText: 'Scan New',
        showCancelButton: true,
        cancelButtonText: 'Close',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#6c757d',
      }).then((result) => {
        if (result.isConfirmed) {
          // Reload the page when "Scan New" is clicked
          window.location.reload();
        }
      });
      
    } catch (error) {
      console.error('Error submitting data:', error);
      setError('Error submitting data. Please try again.');
      
      // Show error message with SweetAlert
      Swal.fire({
        title: 'Error!',
        text: 'Failed to submit data. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    }
  };

  const resetScanner = () => {
    setScannedCode("");
    setProduct(null);
    setError(null);
    setShowScanner(true);
    setIsScanning(false);
    setShowForm(false);
    setSelectedVariations([]);
    setTagInputs({});
    setFormData({
      raw_product_id: "",
      product_name: "",
      product_code: "",
      supplier_id: "",
      purchase_date: "",
      is_expiry_available: false,
      expiry_month_year: "",
      is_expired: false,
      unit: "",
      variant_stock: []
    });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
      <h1 className="text-xl font-bold mb-4">Barcode Scanner</h1>
    <input
              ref={inputRef}
              type="text"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const code = e.target.value.trim();
                  // setScannedCode(code);
                  fetchProduct(code);
                  e.target.value = ''; // clear input after scan
                }
              }}
              style={{
                position: 'absolute',
                opacity: 0,
                pointerEvents: 'none',
                height: 0,
                width: 0,
              }}
              autoFocus
            />
      {showScanner ? (
        <div className="w-full max-w-4xl">
          <div
            ref={scannerRef}
            className="scanner-box"
          >
            {/* Scanner overlay */}
            <div
              style={{
                border: "2px dashed #00ff00",
                position: "absolute",
                top: "15%",
                left: "10%",
                width: "80%",
                height: "70%",
                zIndex: 10,
                pointerEvents: "none",
              }}
            />
            
            {/* Status indicator */}
            <div
              style={{
                position: "absolute",
                top: "10px",
                left: "10px",
                background: isScanning ? "#00ff00" : "#ff0000",
                color: "white",
                padding: "5px 10px",
                borderRadius: "4px",
                fontSize: "12px",
                zIndex: 20,
              }}
            >
              {isScanning ? "Scanning..." : "Stopped"}
            </div>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600 mb-2">
              Position the barcode within the green dashed area
            </p>
            <p className="text-xs text-blue-600 mb-2">
              Supported formats: Code128, Code39 (Format: SKU-VENDOR-PRODUCT)
            </p>
            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="w-full max-w-md text-center bg-white shadow p-4 rounded">
          <p className="mb-2 text-green-600 font-semibold">
            Scanned: {scannedCode}
          </p>

          {product ? (
            <div className="text-left space-y-1 mb-4">
              <p><strong>Name:</strong> {product.name}</p>
              <p><strong>SKU:</strong> {product.sku}</p>
              <p><strong>Category:</strong> {product.category}</p>
              {product.sale_cost && (
                <p><strong>Price:</strong> ₹{product.sale_cost}</p>
              )}
              {product.quantity && (
                <p><strong>Stock:</strong> {product.quantity}</p>
              )}
              {product.vendor && (
                <p><strong>Vendor:</strong> {product.vendor}</p>
              )}
              {product.variant_name && (
                <p><strong>Variant:</strong> {product.variant_name}</p>
              )}
              {product.serial_no && (
                <p><strong>Serial:</strong> {product.serial_no}</p>
              )}
              
              <div className="mt-4 text-center">
                <p className="text-sm text-blue-600 mb-2">
                  Product found! Form will open automatically.
                </p>
              </div>
              <div className="space-y-2">
                <button
                  onClick={resetScanner}
                  className="px-4 py-2 btn btn-primary bg-blue-600 text-white rounded hover:bg-blue-700 w-full"
                >
                  Scan New
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="mb-4">
                <p className="text-red-500 mb-2">{error || "No product found."}</p>
                <p className="text-sm text-gray-600">
                  The scanned barcode was not found in the database.
                </p>
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={resetScanner}
                  className="px-4 py-2 btn btn-primary bg-blue-600 text-white rounded hover:bg-blue-700 w-full"
                >
                  Scan Again
                </button>
                
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 btn btn-secondary bg-gray-600 text-white rounded hover:bg-gray-700 w-full"
                >
                  Reload Page
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Raw Material Stock Form Modal */}
      {showForm && (
        <Modal show={showForm} onClose={() => setShowForm(false)} heading="Add Raw Material Stock">
          <form className="row" onSubmit={handleSubmit}>
            <div className="form-group col-md-6 mt-4">
              <label htmlFor="product_name">Product Name</label>
              <input
                className="form-control"
                type="text"
                name="product_name"
                id="product_name"
                value={formData.product_name}
                readOnly
              />
            </div>

            <div className="form-group col-md-6 mt-4">
              <label htmlFor="product_code">Product Code / SKU</label>
              <input
                className="form-control"
                type="text"
                name="product_code"
                id="product_code"
                value={formData.product_code}
                readOnly
              />
            </div>


            <div className="form-group col-md-6 mt-4">
              <label htmlFor="supplier_id">Supplier</label>
              <input
                className="form-control"
                type="text"
                value={product.vendor || ""}
                readOnly
              />
            </div>

            <div className="form-group col-md-6 mt-4">
              <label htmlFor="purchase_date">Purchase Date</label>
              <input
                className="form-control"
                type="date"
                name="purchase_date"
                id="purchase_date"
                value={formData.purchase_date}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="form-group col-md-6 mt-4">
              <label htmlFor="expiry_month_year">Expiry Month & Year</label>
              <input
                className="form-control"
                type="month"
                name="expiry_month_year"
                id="expiry_month_year"
                value={formData.expiry_month_year}
                onChange={handleFormChange}
              />
            </div>

            {/* Only show single quantity input if not a variant product */}
            {!(product && product.product_type === 'variants') && (
              <div className="form-group col-md-6 mt-4">
                <label htmlFor="quantity">Quantity</label>
                <input
                  className="form-control"
                  type="number"
                  name="quantity"
                  id="quantity"
                  value={formData.quantity}
                  onChange={handleFormChange}
                  required
                />
              </div>
            )}

            {/* <div className="form-check col-md-3 mt-5 pt-2">
              <input
                type="checkbox"
                name="is_expiry_available"
                id="is_expiry_available"
                checked={formData.is_expiry_available}
                onChange={handleFormChange}
                style={{ width: "20px", height: "20px" }}
              />
              <label className="form-check-label ms-3 fs-5" htmlFor="is_expiry_available">Is Expiry Available?</label>
            </div>
            <div className="form-check col-md-3 mt-5 pt-2">
              <input
                type="checkbox"
                name="is_expired"
                id="is_expired"
                checked={formData.is_expired}
                onChange={handleFormChange}
                style={{ width: "20px", height: "20px" }}
              />
              <label className="form-check-label ms-2 fs-5" htmlFor="is_expired">Is Expired?</label>
            </div> */}

            {/* Variant Stock Section */}
            {product && product.product_type === 'variants' && variations.length > 0 && (
              <div className="col-12 mt-4">
                <h5>Variant Stock</h5>
                {selectedVariations.map((variation, index) => (
                  <div key={index} className="row border p-3 mb-3">
                    <div className="col-md-3">
                      <label>Variation</label>
                      <select
                        className="form-control"
                        value={variation.combination ? JSON.stringify(variation.combination) : ''}
                        onChange={(e) => {
                          const selected = JSON.parse(e.target.value);
                          updateVariation(index, 'combination', selected);
                        }}
                        required
                      >
                        <option value="">Select Variation</option>
                        {variations.map((v, i) => (
                          <option key={i} value={JSON.stringify(v)}>
                            {v.map(item => `${item.attributeId == 1?"Size":"Color"}: ${item.value}`).join(' - ')}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label>Quantity</label>
                      <input
                        type="number"
                        className="form-control"
                        value={variation.quantity}
                        onChange={(e) => {
                          const newQuantity = parseInt(e.target.value) || 0;
                          const currentSerialCount = variation.serial_no ? variation.serial_no.length : 0;
                          
                          if (newQuantity < currentSerialCount) {
                            alert(`Cannot set quantity to ${newQuantity}. You already have ${currentSerialCount} serial numbers. Please remove some serial numbers first.`);
                            return;
                          }
                          
                          updateVariation(index, 'quantity', e.target.value);
                        }}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label>Serial Numbers</label>
                      <div className="tags-input-container">
                        <div className="tags-display">
                          {Array.isArray(variation.serial_no) ? variation.serial_no.map((tag, tagIndex) => (
                            <span key={tagIndex} className="tag">
                              {tag}
                              <button
                                type="button"
                                className="tag-remove"
                                onClick={() => removeTag(index, tag)}
                              >
                                ×
                              </button>
                            </span>
                          )) : null}
                        </div>
                        <input
                          type="text"
                          className="form-control tag-input"
                          value={tagInputs[index] || ''}
                          onChange={(e) => handleTagInputChange(index, e.target.value)}
                          onKeyPress={(e) => handleTagInputKeyPress(index, e)}
                          placeholder="Type and press Enter to add serial number"
                        />
                      </div>
                      <style jsx>{`
                        .tags-input-container {
                          border: 1px solid #ced4da;
                          border-radius: 0.375rem;
                          padding: 0.375rem 0.75rem;
                          background: white;
                        }
                        .tags-display {
                          display: flex;
                          flex-wrap: wrap;
                          gap: 0.25rem;
                          margin-bottom: 0.25rem;
                        }
                        .tag {
                          background: #007bff;
                          color: white;
                          padding: 0.125rem 0.5rem;
                          border-radius: 0.25rem;
                          font-size: 0.875rem;
                          display: flex;
                          align-items: center;
                          gap: 0.25rem;
                        }
                        .tag-remove {
                          background: none;
                          border: none;
                          color: white;
                          cursor: pointer;
                          font-size: 1rem;
                          padding: 0;
                          line-height: 1;
                        }
                        .tag-input {
                          border: none;
                          outline: none;
                          box-shadow: none;
                          padding: 0;
                        }
                      `}</style>
                    </div>
                    <div className="col-md-2">
                      <label>Price / Unit</label>
                      <input
                        type="number"
                        className="form-control"
                        value={variation.price}
                        onChange={(e) => updateVariation(index, 'price', e.target.value)}
                        placeholder="Price"
                      />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => removeVariation(index)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-success mb-3"
                  onClick={addVariation}
                >
                  + Add Variation
                </button>
              </div>
            )}

            <div>
              <button type="submit" className="btn btn-primary mt-4">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default BarcodeScanner;
